import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, type QueryFunction, type QueryKey } from "@tanstack/react-query";
import type { ArtifactSelection } from "@/api/queries";
import {
  linksFor,
  markKey,
  member,
  reactionFor,
  toggle,
  type Link,
  type Linked,
  type Reaction,
  type Selection,
} from "@/artifacts/links";
import type { Artifact } from "@/artifacts/types";

/**
 * The linked scope's state and each card's view of it (LINK-2). The
 * components that render it are in `LinkedScope.tsx`.
 */

/** How a map drilled into one region is fetched (LINK-MAP). */
export type ZoomQuery = (
  index: number,
  link: string,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => { queryKey: QueryKey; queryFn?: QueryFunction<Artifact, any> };

/** How a narrowed chart is fetched: the owner knows the route (a message, a tile). */
export type ViewQuery = (
  index: number,
  kind: string,
  selection: ArtifactSelection,
  // The key's own type is the route's business; this only needs a key and a fetch.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => { queryKey: QueryKey; queryFn?: QueryFunction<Artifact, any> };

export interface Scope {
  links: readonly Link[];
  selection: Selection | null;
  select: (next: Selection | null) => void;
  /** Narrow from stored rows (LINK-3): nothing runs. */
  view: ViewQuery | null;
  /**
   * Fetch again, scoped (LINK-4): the artifact's own query re-run for the
   * selection, for rows the connection's cap cut off. Logged-in owners only —
   * a share or an exported file has none, and stays with the rows it holds.
   */
  drill: ViewQuery | null;
  /** Write a follow-up question for the reader to edit and send (LINK-4). */
  onAsk: ((text: string) => void) | null;
  /** Fetch a shaded map drilled into one region (LINK-MAP). */
  zoom: ZoomQuery | null;
}

export const ScopeContext = createContext<Scope | null>(null);

export interface LinkedArtifact {
  /** The links this artifact takes part in. */
  linked: Linked[];
  /** What it does about the current selection, or null. */
  reaction: Reaction | null;
  /** A selection is active somewhere in the scope. */
  active: boolean;
  /** What the artifact follows, for its header: "State". */
  follows: string[];
  /**
   * The artifact narrowed by the server, while it is being fetched, and if it
   * failed. `fetched` says it came from a scoped re-run (LINK-4) rather than
   * from stored rows (LINK-3).
   */
  view: { artifact: Artifact | null; pending: boolean; error: string | null; fetched: boolean };
  onMark: ((names: readonly unknown[], additive: boolean) => void) | null;
  onRow: ((row: number, additive: boolean) => void) | null;
  /**
   * A drill this map can offer for the current selection: one region of the
   * map's own grain is selected and the server published a finer level.
   */
  zoomable: Zoomable | null;
}

export interface Zoomable {
  link: string;
  key: string;
  /** The selected region's name, and what the drill shows: "counties". */
  region: string;
  label: string;
  /** What the breadcrumb's first step reads: "United States". */
  root: string;
}

/**
 * This artifact's part in the scope, or null outside one.
 *
 * `index` is the artifact's position in what the scope's links describe — an
 * answer's artifact index, a grid's tile order. Undefined opts the card out.
 */
export function useLinkedArtifact(
  artifact: Artifact,
  index: number | undefined,
): LinkedArtifact | null {
  const scope = useContext(ScopeContext);
  const linked = useMemo(
    () => (scope && index !== undefined ? linksFor(scope.links, index) : []),
    [scope, index],
  );
  const selection = scope?.selection ?? null;
  const reaction = useMemo(() => {
    if (index === undefined) return null;
    for (const entry of linked) {
      const found = reactionFor(entry, selection, index);
      if (found) return found;
    }
    return null;
  }, [linked, selection, index]);

  // Which narrowing, if any, comes from the server. Rows the cap cut off are
  // fetched again for the selection (LINK-4), because narrowing a page in
  // place would show a partial member as if it were whole. Otherwise a chart
  // is re-shaped from stored rows (LINK-3) and a table filters in place.
  const filtering = reaction?.kind === "filter-chart" || reaction?.kind === "filter-rows";
  const capped = Boolean(artifact.data?.truncated);
  const route = !filtering
    ? null
    : capped && scope?.drill
      ? scope.drill
      : reaction?.kind === "filter-chart"
        ? (scope?.view ?? null)
        : null;
  const viewOptions =
    route && selection && index !== undefined
      ? route(index, artifact.type, {
          link: selection.link,
          grain: selection.grain,
          keys: selection.keys,
        })
      : null;
  const viewed = useQuery<Artifact | null>({
    queryKey: viewOptions?.queryKey ?? ["artifact-view", "none"],
    queryFn:
      (viewOptions?.queryFn as QueryFunction<Artifact | null> | undefined) ?? (async () => null),
    enabled: Boolean(viewOptions),
    staleTime: Infinity,
    retry: false,
  });

  const select = scope?.select;
  const onMark = useCallback(
    (names: readonly unknown[], additive: boolean) => {
      if (!select || index === undefined) return;
      for (const { link, target } of linked) {
        if (target.role !== "axis") continue;
        const key = markKey(target, names);
        if (key) {
          select(toggle(selection, link, target.grain, key, additive, index));
          return;
        }
      }
    },
    [select, linked, selection, index],
  );
  const onRow = useCallback(
    (row: number, additive: boolean) => {
      if (!select || index === undefined) return;
      for (const { link, target } of linked) {
        const key = target.role === "column" ? target.rows?.[row] : null;
        if (key) {
          select(toggle(selection, link, target.grain, key, additive, index));
          return;
        }
      }
    },
    [select, linked, selection, index],
  );

  if (!scope || index === undefined) return null;
  const offered = linked.find(({ target }) => target.zoom);
  const only = selection?.keys.length === 1 ? selection.keys[0] : undefined;
  const zoomable =
    offered &&
    scope.zoom &&
    selection &&
    only !== undefined &&
    selection.link === offered.link.id &&
    selection.grain === offered.target.grain
      ? {
          link: offered.link.id,
          key: only,
          region: member(offered.link, selection.grain, only)?.label ?? only,
          label: offered.target.zoom?.label ?? "",
          root: offered.link.dimension === "geo:us" ? "United States" : offered.link.label,
        }
      : null;
  const axis = linked.some(({ target }) => target.role === "axis");
  const column = linked.some(({ target }) => target.role === "column");
  return {
    linked,
    reaction,
    active: selection !== null,
    follows: [...new Set(linked.map(({ link }) => link.label))],
    view: {
      artifact: viewOptions ? (viewed.data ?? null) : null,
      pending: Boolean(viewOptions) && viewed.isFetching,
      error: viewOptions && viewed.error ? viewed.error.message : null,
      fetched: Boolean(viewOptions) && route === scope.drill,
    },
    onMark: axis ? onMark : null,
    onRow: column ? onRow : null,
    zoomable,
  };
}

/** The drilled map for `zoomed`, while it loads, and if it was refused (LINK-MAP). */
export function useZoomedArtifact(index: number | undefined, zoomed: Zoomable | null) {
  const scope = useContext(ScopeContext);
  const options =
    zoomed && scope?.zoom && index !== undefined ? scope.zoom(index, zoomed.link, zoomed.key) : null;
  const query = useQuery<Artifact | null>({
    queryKey: options?.queryKey ?? ["artifact-zoom", "none"],
    queryFn:
      (options?.queryFn as QueryFunction<Artifact | null> | undefined) ?? (async () => null),
    enabled: Boolean(options),
    staleTime: Infinity,
    retry: false,
  });
  return {
    artifact: options ? (query.data ?? null) : null,
    pending: Boolean(options) && query.isFetching,
    error: options && query.error ? query.error.message : null,
  };
}

